import { Box, Button, Card, Group, Loader, NavLink, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { RecordLabel } from '../../components/Diff/RecordLabel.js';
import { glyphIcon } from '../../components/StatusBadge/glyphs.js';
import { countOf } from '../../domain/labels.js';
import { EMPTY_FILTER } from '../../domain/rowFilter.js';
import { rowLabel } from '../../domain/rowLabel.js';
import { SYNC_STATUS, type SyncStatus } from '../../domain/status.js';
import { useStudioRows } from '../../hooks/useStudioRows.js';
import { railStatus } from './railStatus.js';

/**
 * The Games page's studio chooser, left of the grid above `sm` (below it the Studio select is
 * the chooser — one per breakpoint, never both).
 *
 * A rail row is not a grid row and must not read as one: one line, one status glyph, no
 * per-environment cells. **All studios** heads the list, because the grid can show every
 * studio's games. The count is the studio's master game count. The rail's own find box narrows
 * the rail, not the grid, so it is the rail's state and not a URL filter; the choice itself is
 * `?studio=`.
 *
 * It sticks beside the grid rather than scrolling away with it: choosing another studio is what
 * the page is built around, and a long game list must not put it out of reach.
 */
export const StudioRail = ({
    selected,
    onSelect,
}: {
    selected: string | null;
    onSelect: (studio: string | null) => void;
}) => {
    const [search, setSearch] = useState('');
    const [debounced] = useDebouncedValue(search.trim(), 300);
    const studios = useStudioRows({ ...EMPTY_FILTER, search: debounced });
    const rows = studios.rows;

    return (
        <Card
            p={0}
            style={{
                overflow: 'hidden',
                width: 240,
                flexShrink: 0,
                position: 'sticky',
                top: 'calc(var(--app-shell-header-offset, 80px) + var(--mantine-spacing-md))',
                alignSelf: 'flex-start',
            }}
        >
            <Box p="xs" style={{ borderBottom: '1px solid var(--rgs-line)', background: 'var(--rgs-surface-2)' }}>
                <TextInput
                    size="xs"
                    placeholder="Find a studio"
                    aria-label="Find a studio"
                    leftSection={<IconSearch size={13} stroke={1.7} />}
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                />
            </Box>

            <ScrollArea.Autosize mah="calc(100vh - 200px)">
                <Stack gap={0}>
                    {!debounced && (
                        <RailRow
                            selected={selected === null}
                            onSelect={() => onSelect(null)}
                            ariaLabel="Show every studio's games"
                        >
                            <Text fz={12.5} fw={selected === null ? 700 : 600}>
                                All studios
                            </Text>
                        </RailRow>
                    )}
                    {studios.isLoading ? (
                        <Group justify="center" p="md">
                            <Loader size="xs" />
                        </Group>
                    ) : rows.length === 0 ? (
                        <Text fz="xs" c="dimmed" p="sm">
                            No studio matches.
                        </Text>
                    ) : (
                        rows.map((row) => {
                            const label = rowLabel(row.title, row.key);
                            const status = railStatus(row.rollups);
                            const isSelected = row.key === selected;
                            return (
                                <RailRow
                                    key={row.key}
                                    selected={isSelected}
                                    onSelect={() => onSelect(row.key)}
                                    ariaLabel={`Show ${label.label}'s ${countOf('game', row.childCount)} — ${SYNC_STATUS[status].label.toLowerCase()}`}
                                    status={status}
                                    count={row.childCount}
                                >
                                    <RecordLabel
                                        title={row.title}
                                        entityKey={row.key}
                                        layout="stacked"
                                        compact
                                        strong={isSelected}
                                    />
                                </RailRow>
                            );
                        })
                    )}
                    {studios.hasMore && (
                        <Box p={6}>
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                fullWidth
                                loading={studios.isLoadingMore}
                                onClick={studios.loadMore}
                            >
                                Load more studios
                            </Button>
                        </Box>
                    )}
                </Stack>
            </ScrollArea.Autosize>
        </Card>
    );
};

const RailRow = ({
    selected,
    onSelect,
    ariaLabel,
    status,
    count,
    children,
}: {
    selected: boolean;
    onSelect: () => void;
    ariaLabel: string;
    status?: SyncStatus;
    count?: number;
    children: ReactNode;
}) => {
    const meta = status ? SYNC_STATUS[status] : null;
    const Glyph = meta ? glyphIcon(meta.glyph) : null;
    return (
        <NavLink
            component="button"
            type="button"
            active={selected}
            onClick={onSelect}
            aria-current={selected ? 'true' : undefined}
            aria-label={ariaLabel}
            label={children}
            leftSection={
                // A glyph in the status ink, not a bare dot: colour is never the sole carrier.
                // In sync stays quiet, as an in-sync grid cell does.
                <Box
                    c={meta?.color}
                    lh={0}
                    w={12}
                    style={{ opacity: !meta || meta.dimmed || status === 'in_sync' ? 0.5 : 1 }}
                    aria-hidden
                >
                    {Glyph && <Glyph size={12} stroke={2.4} />}
                </Box>
            }
            rightSection={
                count !== undefined ? (
                    <Text className="rgs-numeric" fz={11} c="dimmed">
                        {count}
                    </Text>
                ) : undefined
            }
            styles={{
                root: {
                    padding: '7px 10px',
                    borderBottom: '1px solid var(--rgs-line)',
                    // NavLink's active variant would tint the label brand; the rail keeps ink.
                    color: 'var(--mantine-color-text)',
                    // The chosen studio is what the grid beside it shows: the brand edge says so.
                    boxShadow: selected ? 'inset 3px 0 0 var(--mantine-color-rgsOrange-filled)' : undefined,
                },
                section: { marginInline: 0 },
                body: { marginInline: 7 },
            }}
        />
    );
};
